// ============================================================================
// CREDIT / DEBIT NOTE (PMS Phase 2 slice 7b)
// ============================================================================
// An issued invoice is never changed: a note adjusts it. A CREDIT note (the
// default) reduces its value; a DEBIT note — chosen explicitly — increases it.
//   Credit: Cancel and re-issue (the whole invoice credited, its lines back on
//           the folio to invoice again — wrong payer, GSTIN, address) ·
//           Selected lines · An amount (spread over the lines).
//   Debit:  Add to lines (an amount each) · An amount (spread). A new charge is
//           not a debit note — post it as a late charge.
// Each note line is a share of the invoice's own folio lines with their tax at
// the original rates; the server previews exactly what will be issued. A reason
// is required; a credit against an earlier financial year needs a confirmation;
// above the property's limit the approve-note permission.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { invoiceService } from 'api/services/invoiceService';
import { formatCurrency } from 'utils/currencyFormat';
import { reserveTab, showPdf } from 'utils/reportFile';
import { INVOICE_ACTIONS, type Invoice, type NotePreview, type NoteRequest } from 'api/types/invoice.types';

interface Props {
  isOpen: boolean;
  invoiceId: string | null;
  /** The user's pms-invoices actions. */
  allowedActions: string[];
  onClose: () => void;
  onIssued: (note: Invoice) => void;
}

type Kind = NoteRequest['kind'];

const NoteModal: React.FC<Props> = ({ isOpen, invoiceId, allowedActions, onClose, onIssued }) => {
  const canCredit = allowedActions.includes(INVOICE_ACTIONS.creditNote);
  const canDebit = allowedActions.includes(INVOICE_ACTIONS.debitNote);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [direction, setDirection] = useState<'C' | 'D'>('C');
  const [kind, setKind] = useState<Kind>('LINES');
  const [picked, setPicked] = useState<Record<number, string>>({});
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirmYear, setConfirmYear] = useState(false);
  const [printAfter, setPrintAfter] = useState(true);
  const [preview, setPreview] = useState<NotePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!isOpen || !invoiceId) return;
    setInvoice(null); setPreview(null); setPreviewError(null);
    setDirection(canCredit ? 'C' : 'D'); setKind('LINES'); setPicked({}); setAmount(''); setReason(''); setConfirmYear(false);
    invoiceService.get(invoiceId).then(setInvoice)
      .catch((err: any) => notificationService.toast.error(err?.response?.data?.message || err?.message || 'The invoice did not load.'));
  }, [isOpen, invoiceId, canCredit]);

  const request = useMemo((): NoteRequest => ({
    direction,
    kind,
    lines: Object.entries(picked).map(([lineNo, a]) => ({ lineNo: Number(lineNo), amount: direction === 'D' ? Number(a) || 0 : null })),
    amount: kind === 'AMOUNT' ? Number(amount) || 0 : null,
    reason: reason.trim() || null,
    confirmEarlierYear: confirmYear,
  }), [direction, kind, picked, amount, reason, confirmYear]);

  // The server's own calculation of the note, as it would be issued.
  const ready = kind === 'FULL' || (kind === 'AMOUNT' ? Number(amount) > 0 : Object.keys(picked).length > 0);
  useEffect(() => {
    if (!isOpen || !invoiceId || !ready) { setPreview(null); setPreviewError(null); return; }
    const mine = ++seq.current;
    const handle = window.setTimeout(() => {
      invoiceService.previewNote(invoiceId, request)
        .then((p) => { if (mine === seq.current) { setPreview(p); setPreviewError(null); } })
        .catch((err: any) => { if (mine === seq.current) { setPreview(null); setPreviewError(err?.response?.data?.message || err?.message || 'The preview failed.'); } });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [isOpen, invoiceId, request, ready]);

  const issue = useCallback(async () => {
    if (!invoiceId || !preview) return;
    const tab = printAfter ? reserveTab() : null;
    setIssuing(true);
    try {
      const note = await invoiceService.issueNote(invoiceId, request);
      notificationService.toast.success(`${direction === 'D' ? 'Debit' : 'Credit'} note ${note.invoiceNumber} issued — ${formatCurrency(note.totalAmount)}.`);
      if (tab) { try { showPdf(await invoiceService.pdf(note.id), tab); } catch { tab.close(); } }
      onIssued(note);
    } catch (err: any) {
      tab?.close();
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The note was not issued.');
    } finally {
      setIssuing(false);
    }
  }, [invoiceId, preview, printAfter, request, direction, onIssued]);

  if (!isOpen || !invoiceId) return null;
  const debit = direction === 'D';
  const kinds: Array<{ v: Kind; l: string; d: string }> = debit
    ? [
      { v: 'LINES', l: 'Add to lines', d: 'An amount added to chosen lines, at their own tax rates.' },
      { v: 'AMOUNT', l: 'An amount', d: 'Spread over every line in proportion.' },
    ]
    : [
      { v: 'LINES', l: 'Selected lines', d: 'What is left of the chosen lines.' },
      { v: 'AMOUNT', l: 'An amount', d: 'A goodwill amount spread over the lines and their tax.' },
      { v: 'FULL', l: 'Cancel and re-issue', d: 'The whole invoice credited; its lines back on the folio to invoice again (wrong payer, GSTIN, address).' },
    ];
  const remaining = invoice ? invoice.totalAmount + (invoice.debitedAmount ?? 0) - (invoice.creditedAmount ?? 0) : 0;
  const blocked = !preview || !preview.canIssue || reason.trim().length < 3 || (preview.earlierYear && !confirmYear);

  return (
    <>
      <div className="modal-backdrop" style={{ zIndex: 10070 }} />
      <div className="modal-container modal-lg" style={{ zIndex: 10071 }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{debit ? 'Debit note' : 'Credit note'} — against {invoice?.invoiceNumber ?? '…'}</h2>
          <button className="modal-close" onClick={onClose} disabled={issuing}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
          {invoice && (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
              {invoice.billToName}{invoice.billToGstin ? ` · ${invoice.billToGstin}` : ''} · invoice {formatCurrency(invoice.totalAmount)}
              {(invoice.creditedAmount || invoice.debitedAmount) ? ` · credited ${formatCurrency(invoice.creditedAmount ?? 0)}, debited ${formatCurrency(invoice.debitedAmount ?? 0)}` : ''}
              {' · '}left <b>{formatCurrency(remaining)}</b>
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.6rem', fontSize: '0.85rem' }}>
            {canCredit && (
              <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" checked={!debit} onChange={() => { setDirection('C'); setKind('LINES'); setPicked({}); }} /> Credit note <span style={{ color: 'var(--text-muted)' }}>(reduce)</span>
              </label>
            )}
            {canDebit && (
              <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" checked={debit} onChange={() => { setDirection('D'); setKind('LINES'); setPicked({}); }} /> Debit note <span style={{ color: 'var(--text-muted)' }}>(increase)</span>
              </label>
            )}
          </div>
          {debit && (
            <div style={{ fontSize: '0.75rem', color: 'var(--warning-text, #92400e)', background: 'var(--warning-bg, #fef3c7)', padding: '0.4rem 0.6rem', borderRadius: 6, marginBottom: '0.6rem' }}>
              A debit note adds value to a supply already invoiced (e.g. a rate under-billed). A new charge is a late charge on a supplementary invoice, not a debit note.
            </div>
          )}

          <div className="form-group">
            {kinds.map((k) => (
              <label key={k.v} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '0.2rem' }}>
                <input type="radio" checked={kind === k.v} onChange={() => setKind(k.v)} style={{ marginTop: '0.2rem' }} />
                <span style={{ fontSize: '0.85rem' }}>{k.l} <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>— {k.d}</span></span>
              </label>
            ))}
          </div>

          {kind === 'LINES' && invoice && (
            <table className="data-table" style={{ width: '100%', fontSize: '0.8rem', marginBottom: '0.6rem' }}>
              <thead><tr><th /><th style={{ textAlign: 'left' }}>Line</th><th style={{ textAlign: 'right' }}>Amount</th>{debit && <th style={{ textAlign: 'right' }}>Add (incl. tax)</th>}</tr></thead>
              <tbody>
                {invoice.lines.map((l) => {
                  const on = l.lineNo in picked;
                  return (
                    <tr key={l.lineNo}>
                      <td><input type="checkbox" checked={on} onChange={(e) => setPicked((p) => {
                        const next = { ...p };
                        if (e.target.checked) next[l.lineNo] = ''; else delete next[l.lineNo];
                        return next;
                      })} /></td>
                      <td>{l.lineNo}. {l.description}{l.isReimbursement && <span className="badge badge-secondary" style={{ fontSize: '0.58rem', marginLeft: 4 }}>reimbursement</span>}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(l.totalAmount)}</td>
                      {debit && (
                        <td style={{ textAlign: 'right' }}>
                          <input type="number" className="form-input" style={{ width: 110, height: '1.8rem', textAlign: 'right' }} min={0} step={0.01}
                            disabled={!on} value={picked[l.lineNo] ?? ''} onChange={(e) => setPicked((p) => ({ ...p, [l.lineNo]: e.target.value }))} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {kind === 'AMOUNT' && (
            <div className="form-group">
              <label className="form-label">{debit ? 'Amount to add' : 'Amount to credit'} (including tax)</label>
              <input type="number" className="form-input" style={{ maxWidth: 200 }} min={0} step={0.01} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Reason <span className="text-danger">*</span></label>
            <input type="text" className="form-input" value={reason} maxLength={500} placeholder="Printed on the note"
              onChange={(e) => setReason(e.target.value)} />
          </div>

          {previewError && <div className="form-error-message">{previewError}</div>}
          {preview && (
            <div style={{ padding: '0.6rem 0.8rem', borderRadius: 6, background: 'var(--bg-secondary)', fontSize: '0.83rem' }}>
              {preview.note.lines.map((l) => (
                <div key={l.lineNo} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                  <span>{l.description}</span><span>{formatCurrency(l.totalAmount)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1.25rem', marginTop: '0.35rem' }}>
                <span>Taxable {formatCurrency(preview.note.taxableAmount)}</span>
                <span>Tax {formatCurrency(preview.note.taxAmount)}</span>
                {!!preview.note.reimbursementAmount && <span>Reimbursements {formatCurrency(preview.note.reimbursementAmount)}</span>}
                <b>{debit ? 'Debit' : 'Credit'} {formatCurrency(preview.note.totalAmount)}</b>
              </div>
              {preview.blockers.map((b) => <div key={b} className="form-error-message">{b}</div>)}
              {preview.warnings.map((w) => <div key={w} style={{ color: 'var(--warning-text, #92400e)', marginTop: '0.25rem' }}>{w}</div>)}
              {preview.earlierYear && (
                <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '0.35rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={confirmYear} onChange={(e) => setConfirmYear(e.target.checked)} />
                  I confirm the tax on this invoice may still be reduced (kept on the note).
                </label>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', marginRight: 'auto', cursor: 'pointer' }}>
            <input type="checkbox" checked={printAfter} onChange={(e) => setPrintAfter(e.target.checked)} /> Print the note
          </label>
          <button className="btn btn-secondary" onClick={onClose} disabled={issuing}>Cancel</button>
          <button className={`btn ${debit ? 'btn-warning' : 'btn-primary'}`} onClick={() => { void issue(); }} disabled={issuing || blocked}
            title={reason.trim().length < 3 ? 'Give the reason' : undefined}>
            {issuing ? 'Issuing…' : `Issue ${debit ? 'debit' : 'credit'} note${preview ? ` · ${formatCurrency(preview.note.totalAmount)}` : ''}`}
          </button>
        </div>
      </div>
    </>
  );
};

export default NoteModal;
